import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Award, Users, Globe, ArrowRight, BookOpen, Sparkles, GraduationCap, Building2, Lightbulb, CheckCircle } from 'lucide-react';

const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We uphold high standards in professional education by combining academic rigor with practical relevance and real-world applicability.'
    },
    {
      icon: Heart,
      title: 'Humanity',
      description: 'We affirm the dignity of every person and the responsibility to respond to human suffering with integrity, compassion, and purpose.'
    },
    {
      icon: Users,
      title: 'Inclusivity',
      description: 'We are committed to widening access to quality education across economic, geographic, and professional boundaries.'
    },
    {
      icon: Globe,
      title: 'Global Perspective',
      description: 'We prepare learners to engage with humanitarian and development issues in ways that are internationally informed, context-sensitive, and responsive to real-world challenges.'
    },
  ];

  const programs = [
    'Master of Humanitarian Action & Development',
    'Master of Development Project & Program Management',
    'Doctor of Humanitarian Action & Development',
    'Professional short courses in emerging areas'
  ];

  const whoWeServe = [
    'Humanitarian and development professionals',
    'NGO and civil society staff',
    'Project and program officers',
    'MEAL, grants, finance, and operations staff',
    'Public sector and social impact professionals',
    'Researchers, educators, and emerging thought leaders',
    'Recent graduates and career changers seeking structured professional pathways'
  ];

  const stats = [
    { number: '4', label: 'Learning Pathways' },
    { number: '90%', label: 'Scholarship Available' },
    { number: '100%', label: 'Online Delivery' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeInUp}
            className="max-w-4xl"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Who We Are
            </p>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              About GIHDS
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              The Geneva Institute of Humanitarian & Development Studies (GIHDS) is an independent 
              online professional education institute dedicated to advancing accessible, practice-informed, 
              and globally relevant learning in humanitarian action, development, project and program 
              management, leadership, policy, and applied professional practice.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About GIHDS - Extended */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Our Story
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Inspired by Geneva's Legacy
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Drawing inspiration from Geneva's long-standing role as an international center for 
                humanitarian dialogue, policy, and coordination, GIHDS supports learners who seek to 
                strengthen their knowledge, professional judgment, and practical capacity to respond 
                to complex humanitarian and development challenges.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                GIHDS was founded on the belief that high-quality professional education should not 
                be limited by geography or economic circumstance. Through flexible online learning, 
                scholarship-supported access, and professionally relevant programs, the Institute 
                aims to support learners from diverse backgrounds, particularly from Africa, Asia, 
                and other underserved regions.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Our programs are designed for current and aspiring humanitarian workers, development 
                professionals, NGO staff, public sector practitioners, civil society actors, researchers, 
                educators, and career changers seeking structured pathways to build capacity in real-world 
                humanitarian and development contexts.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <img
                src="/images/geneva-lake.jpg"
                alt="Geneva, Switzerland - The global center for humanitarian action"
                className="w-full h-[450px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 md:p-12 border-l-4 border-[#163A59] shadow-sm"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#163A59] text-white flex items-center justify-center rounded-sm">
                  <Target size={24} />
                </div>
                <h2 className="font-['Source_Serif_4'] text-2xl md:text-3xl font-semibold text-slate-900">
                  Our Mission
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed text-lg">
                To provide accessible, high-quality professional education that empowers learners 
                from diverse backgrounds to become more effective, ethical, and capable contributors 
                to humanitarian action, sustainable development, and social impact.
              </p>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 md:p-12 border-l-4 border-[#C82032] shadow-sm"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#C82032] text-white flex items-center justify-center rounded-sm">
                  <Eye size={24} />
                </div>
                <h2 className="font-['Source_Serif_4'] text-2xl md:text-3xl font-semibold text-slate-900">
                  Our Vision
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed text-lg">
                A world in which humanitarian and development challenges are addressed by skilled, 
                principled, and locally empowered professionals equipped with globally relevant 
                knowledge, practical tools, and strong professional judgment.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-[#163A59] p-8 md:p-12 text-white">
                <Lightbulb className="w-12 h-12 mb-6 opacity-80" />
                <h3 className="font-['Source_Serif_4'] text-2xl md:text-3xl font-bold mb-6">
                  Learning Model
                </h3>
                <p className="text-white/90 leading-relaxed mb-6">
                  Our learning model emphasizes:
                </p>
                <ul className="space-y-3">
                  {['Practical application', 'Critical thinking', 'Ethical awareness', 'Professional development'].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#C82032] rounded-full"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                How We Teach
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Our Approach
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                GIHDS combines flexible online delivery with professionally relevant curricula 
                designed for learners balancing study, work, and field responsibilities. Our 
                learning model emphasizes practical application, critical thinking, ethical 
                awareness, and professional development across humanitarian, development, and 
                related sectors.
              </p>
              <p className="text-slate-600 leading-relaxed">
                We focus on making learning accessible, structured, and relevant to the realities 
                faced by professionals working in complex environments, including NGOs, international 
                and local organizations, public institutions, donor-funded programs, and social 
                impact initiatives.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Our Principles
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900">
              Core Values
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 border border-slate-200 hover:border-[#163A59]/30 hover:shadow-lg transition-all"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#163A59]/10 text-[#163A59] rounded-sm mb-6">
                  <value.icon size={28} />
                </div>
                <h3 className="font-['Source_Serif_4'] text-xl font-semibold text-slate-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Programs & Courses
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                What We Offer
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                GIHDS provides flexible online programs and short courses designed to support 
                professional growth in humanitarian and development practice. Our current 
                learning pathways include:
              </p>
              <div className="space-y-4 mb-8">
                {programs.map((program) => (
                  <div key={program} className="flex items-start gap-3">
                    <GraduationCap className="w-5 h-5 text-[#163A59] mt-1 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{program}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                Each program is designed to support learners in building knowledge, confidence, 
                leadership capacity, and practical skills for professional roles across humanitarian, 
                development, NGO, public sector, and social impact settings.
              </p>
              <Link
                to="/programs"
                className="inline-flex items-center gap-2 text-[#163A59] font-medium hover:gap-3 transition-all"
              >
                Explore all programs
                <ArrowRight size={18} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="bg-[#163A59] p-8 md:p-12 text-white">
                <BookOpen className="w-12 h-12 mb-6 opacity-80" />
                <h3 className="font-['Source_Serif_4'] text-2xl font-bold mb-6">
                  Areas of Focus
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    'Humanitarian Action',
                    'Sustainable Development',
                    'Project & Program Management',
                    'MEAL',
                    'Development Finance',
                    'Donor Systems',
                    'Humanitarian Leadership',
                    'Policy Engagement',
                    'Protection & Inclusion',
                    'Operations',
                    'Responsible AI in Humanitarian Settings'
                  ].map((area) => (
                    <div key={area} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-[#C82032] rounded-full flex-shrink-0"></div>
                      <span className="text-white/90">{area}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-12">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center text-white"
              >
                <p className="font-['Source_Serif_4'] text-4xl md:text-5xl lg:text-6xl font-bold mb-2">
                  {stat.number}
                </p>
                <p className="text-sm md:text-base opacity-80">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-[#F1F5F8] p-8 md:p-10">
                <h3 className="font-semibold text-slate-900 mb-6 text-lg">
                  GIHDS serves learners across Africa, Asia, and beyond:
                </h3>
                <div className="space-y-3">
                  {whoWeServe.map((audience) => (
                    <div key={audience} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#163A59] mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{audience}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Our Learners
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Who We Serve
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                GIHDS serves a diverse community of learners united by their commitment to 
                making a positive impact in humanitarian and development contexts. Our students 
                come from various professional backgrounds and career stages, all seeking to 
                enhance their capacity to contribute meaningfully to complex global challenges.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Whether you're an experienced professional looking to deepen your expertise, 
                or someone beginning their journey in the humanitarian sector, GIHDS provides 
                the structured learning pathways and support you need to succeed.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <Sparkles className="w-12 h-12 text-[#C82032] mx-auto mb-6" />
            <p className="text-sm font-medium uppercase tracking-widest text-[#FCA5A5] mb-4">
              Our Promise
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-8">
              Our Commitment
            </h2>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
              GIHDS is committed to accessible, flexible, and professionally relevant learning 
              that helps individuals strengthen their capacity to contribute meaningfully to 
              humanitarian action, development practice, and social impact. Through our programs, 
              we aim to support learners in becoming more capable, ethical, and effective 
              professionals in a rapidly changing world.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
              Ready to Join Our Community?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-10">
              Discover how our programs can advance your career in humanitarian action 
              and sustainable development. Take the first step towards making a greater impact.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/programs"
                className="inline-flex items-center justify-center gap-2 bg-[#163A59] hover:bg-[#102A43] text-white px-8 py-4 rounded-sm font-medium transition-all"
                data-testid="about-program-btn"
              >
                Explore Our Programs
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/admissions"
                className="inline-flex items-center justify-center gap-2 bg-white border border-slate-200 text-slate-900 hover:bg-[#F1F5F8] px-8 py-4 rounded-sm font-medium transition-all"
                data-testid="about-apply-btn"
              >
                Apply Now
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
